/* External dependencies */
import { Turnstile } from "@marsidev/react-turnstile";
import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import validate from "validate.js";
import _isUndefined from "lodash/isUndefined";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { Redirect } from "react-router";
import _debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import { speak } from "@wordpress/a11y";

/* Internal dependencies */
import { emailConstraints, passwordRepeatConstraint } from "./CommonConstraints";
import ValidationInputField from "../ValidationInputField";
import ErrorDisplay, { ErrorPropTypeShape } from "../../errors/ErrorDisplay";
import { validatePasswordStrength } from "../../functions/validatePasswordStrength";
import { useTurnstileSubmit } from "./useTurnstileSubmit";
import getEnv from "shared-frontend/functions/getEnv";
import { Button, Checkbox } from "@yoast/ui-library";
import * as styles from "./LoginFormStyles.module.scss";

const TURNSTILE_SITE_KEY = getEnv( "TURNSTILE_SITE_KEY", "1x00000000000000000000AA" );

// Messages
const messages = defineMessages( {
	labelEmail: {
		id: "signup.email",
		defaultMessage: "Email address",
	},
	labelPassword: {
		id: "signup.password",
		defaultMessage: "Password",
	},
	labelPasswordRepeat: {
		id: "signup.passwordRepeat",
		defaultMessage: "Repeat password",
	},
	createAccount: {
		id: "signup.create",
		defaultMessage: "Create account",
	},
	duplicateEmail: {
		id: "signup.error.duplicateEmail",
		defaultMessage: "The email address could not be added, it is probably already in use.",
	},
	newsletterConsent: {
		id: "signup.newsletterConsent",
		defaultMessage: "I agree to receive the Yoast newsletter and marketing emails. I have read and agree to the {privacyPolicyLink}.",
	},
	privacyPolicy: {
		id: "signup.privacyPolicy",
		defaultMessage: "Privacy Policy",
	},
} );

const defaultPasswordValidation = {
	isFilledIn: false,
	score: 0,
	isStrongEnough: false,
	feedback: {
		suggestions: [],
		warning: "",
	},
	errors: [],
	isAllowedPassword: false,
};

/**
 * Sign up page where the user can sign up to MyYoast.
 *
 * @param {object} props The component props.
 *
 * @returns {ReactElement} The rendered component.
 */

const Signup = ( {
	intl,
	email: initialEmail,
	signupError,
	signupRequestSuccess,
	attemptSignup,
} ) => {
	const [ email, setEmail ] = useState( initialEmail );
	const [ password, setPassword ] = useState( "" );
	const [ passwordRepeat, setPasswordRepeat ] = useState( "" );
	const [ passwordValidation, setPasswordValidation ] = useState( defaultPasswordValidation );
	const [ emailErrors, setEmailErrors ] = useState( [] );
	const [ passwordRepeatErrors, setPasswordRepeatErrors ] = useState( [] );
	const [ errorsInputFields, setErrorsInputFields ] = useState( false );
	// GDPR Article 7 / Recital 32: consent must not be pre-ticked.
	// See https://gdpr-info.eu/recitals/no-32/
	const [ newsletterConsent, setNewsletterConsent ] = useState( false );

	const onSignupSubmit = useCallback( ( payload, turnstileToken ) => {
		attemptSignup( { ...payload, turnstileToken } );
	}, [ attemptSignup ] );

	const {
		turnstileRef,
		turnstileError,
		turnstilePending,
		onTurnstileVerify,
		onTurnstileExpire,
		onTurnstileError,
		onTurnstileTimeout,
		onTurnstileScriptError,
		requestSubmit,
	} = useTurnstileSubmit( { onSubmit: onSignupSubmit, error: signupError } );

	// Memoize constraints to avoid recreating on every render.
	const emailConstraintsMemo = useMemo( () => emailConstraints( intl ), [ intl ] );
	const passwordRepeatConstraintMemo = useMemo( () => passwordRepeatConstraint( intl ), [ intl ] );

	// Debounced password validation.
	const debouncedValidatePassword = useMemo(
		() => _debounce( ( value ) => {
			setPasswordValidation( validatePasswordStrength( value, [] ) );
		}, 500 ),
		[],
	);

	// Debounced announce validation errors.
	const debouncedAnnounceErrors = useMemo(
		() => _debounce( ( errors ) => {
			speak( errors.toString() );
		}, 1000 ),
		[],
	);

	// Cleanup debounced functions on unmount.
	useEffect( () => {
		return () => {
			debouncedValidatePassword.cancel();
			debouncedAnnounceErrors.cancel();
		};
	}, [ debouncedValidatePassword, debouncedAnnounceErrors ] );

	/**
	 * Validates email and password repeat fields.
	 *
	 * @param {string} currentEmail The current email value.
	 * @param {string} currentPassword The current password value.
	 * @param {string} currentPasswordRepeat The current password repeat value.
	 * @param {Array} currentPasswordSuggestions The current password suggestions.
	 */
	const runValidation = useCallback( ( currentEmail, currentPassword, currentPasswordRepeat, currentPasswordSuggestions ) => {
		let newEmailErrors = validate.single( currentEmail, emailConstraintsMemo, { format: "detailed" } );
		newEmailErrors = currentEmail.length > 0 && ! _isUndefined( newEmailErrors ) ? newEmailErrors : [];

		let newPasswordRepeatErrors = validate(
			{
				password: currentPassword,
				passwordRepeat: currentPasswordRepeat,
			},
			{ passwordRepeat: passwordRepeatConstraintMemo },
			{ format: "detailed" },
		);
		newPasswordRepeatErrors = ( currentPasswordRepeat.length > 0 && ! _isUndefined( newPasswordRepeatErrors ) )
			? [ newPasswordRepeatErrors[ 0 ].options.message ] : [];

		setEmailErrors( newEmailErrors );
		setPasswordRepeatErrors( newPasswordRepeatErrors );

		// Announce validation errors.
		const allErrors = [ ...newEmailErrors, ...currentPasswordSuggestions, ...newPasswordRepeatErrors ];
		debouncedAnnounceErrors( allErrors );
	}, [ emailConstraintsMemo, passwordRepeatConstraintMemo, debouncedAnnounceErrors ] );

	/**
	 * Handles email field change.
	 *
	 * @param {string} value The input field value.
	 * @param {Array} errors The input field related errors.
	 */
	const onUpdateEmail = useCallback( ( value, errors = [] ) => {
		setEmail( value );
		setErrorsInputFields( errors.length > 0 );
		runValidation( value, password, passwordRepeat, passwordValidation.feedback.suggestions );
	}, [ password, passwordRepeat, passwordValidation.feedback.suggestions, runValidation ] );

	/**
	 * Handles password field change.
	 *
	 * @param {string} value The input field value.
	 * @param {Array} errors The input field related errors.
	 */
	const onUpdatePassword = useCallback( ( value, errors = [] ) => {
		setPassword( value );
		setErrorsInputFields( errors.length > 0 );
		debouncedValidatePassword( value );
		runValidation( email, value, passwordRepeat, passwordValidation.feedback.suggestions );
	}, [ email, passwordRepeat, passwordValidation.feedback.suggestions, debouncedValidatePassword, runValidation ] );

	/**
	 * Handles password repeat field change.
	 *
	 * @param {string} value The input field value.
	 * @param {Array} errors The input field related errors.
	 */
	const onUpdatePasswordRepeat = useCallback( ( value, errors = [] ) => {
		setPasswordRepeat( value );
		setErrorsInputFields( errors.length > 0 );
		runValidation( email, password, value, passwordValidation.feedback.suggestions );
	}, [ email, password, passwordValidation.feedback.suggestions, runValidation ] );

	/**
	 * Handles newsletter consent checkbox change.
	 */
	const onNewsletterConsentChange = useCallback( () => {
		setNewsletterConsent( ( prev ) => ! prev );
	}, [] );

	/**
	 * Checks whether the form can be submitted.
	 *
	 * @returns {boolean} Whether the form can be submitted.
	 */
	const canSubmit = useCallback( () => {
		return ( ! isEmpty( email ) ) &&
			( ! isEmpty( password ) ) &&
			( ! isEmpty( passwordRepeat ) ) &&
			emailErrors.length <= 0 &&
			passwordRepeatErrors.length <= 0 &&
			! errorsInputFields &&
			passwordValidation.isAllowedPassword &&
			! turnstileError &&
			! turnstilePending;
	}, [
		email,
		password,
		passwordRepeat,
		emailErrors,
		passwordRepeatErrors,
		errorsInputFields,
		passwordValidation.isAllowedPassword,
		turnstileError,
		turnstilePending,
	] );

	/**
	 * Handles the form submission.
	 *
	 * @param {Object} event The form submit event.
	 */
	const handleSubmit = useCallback( ( event ) => {
		event.preventDefault();
		if ( ! canSubmit() ) {
			return;
		}

		requestSubmit( {
			userEmail: email,
			password,
			repeatPassword: passwordRepeat,
			newsletterConsent,
		} );
	}, [ canSubmit, requestSubmit, email, password, passwordRepeat, newsletterConsent ] );

	if ( signupRequestSuccess ) {
		return ( <Redirect to={ "/almost-there" } /> );
	}

	const isDisabled = ! canSubmit();

	return (
		<form onSubmit={ handleSubmit } className={ styles.form }>
			<ErrorDisplay error={ signupError } />
			<div>
				<ValidationInputField
					id="email-address"
					autoComplete="on"
					name="email"
					type="text"
					label={ intl.formatMessage( messages.labelEmail ) }
					onChange={ onUpdateEmail }
					value={ email }
					errors={ emailErrors }
				/>
			</div>
			<div>
				<ValidationInputField
					id="password"
					name="password"
					type="password"
					label={ intl.formatMessage( messages.labelPassword ) }
					onChange={ onUpdatePassword }
					value={ password }
					errors={ passwordValidation.feedback.suggestions }
				/>
			</div>
			<div>
				<ValidationInputField
					id="password-repeat"
					name="repeat password"
					type="password"
					label={ intl.formatMessage( messages.labelPasswordRepeat ) }
					onChange={ onUpdatePasswordRepeat }
					value={ passwordRepeat }
					errors={ passwordRepeatErrors }
				/>
			</div>
			{
				TURNSTILE_SITE_KEY &&
				<div>
					<Turnstile
						ref={ turnstileRef }
						siteKey={ TURNSTILE_SITE_KEY }
						options={ { action: "signup", theme: "light", size: "flexible" } }
						scriptOptions={ { onError: onTurnstileScriptError } }
						onSuccess={ onTurnstileVerify }
						onExpire={ onTurnstileExpire }
						onError={ onTurnstileError }
						onTimeout={ onTurnstileTimeout }
					/>
				</div>
			}
			<div className={ styles.newsletterConsent }>
				<Checkbox
					id="newsletter-consent"
					name="newsletter-consent"
					value="newsletter-consent"
					checked={ newsletterConsent }
					onChange={ onNewsletterConsentChange }
					className="yst-mt-[2px]"
				/>
				<label htmlFor="newsletter-consent">
					<FormattedMessage
						{ ...messages.newsletterConsent }
						values={ {
							privacyPolicyLink: (
								<a
									href="https://www.newfold.com/privacy-center/privacy"
									target="_blank"
									rel="noopener noreferrer"
									className="yst-link"
								>
									{ intl.formatMessage( messages.privacyPolicy ) }
								</a>
							),
						} }
					/>
				</label>
			</div>
			<div>
				<Button
					className={ styles.button }
					type="submit"
					aria-disabled={ isDisabled }
					disabled={ isDisabled }
				>
					<FormattedMessage { ...messages.createAccount } />
				</Button>
			</div>
		</form>
	);
};

Signup.propTypes = {
	intl: intlShape.isRequired,
	signupError: ErrorPropTypeShape,
	email: PropTypes.string,
	attemptSignup: PropTypes.func.isRequired,
	signupRequestSuccess: PropTypes.bool.isRequired,
};

Signup.defaultProps = {
	signupError: null,
	email: "",
};

export default injectIntl( Signup );
